import { useCallback, useEffect, useMemo, useState } from 'react'
import { Assets, MenuGroup, MenuItem } from '@nexds/web'

import { InjectionTokens } from '@/controller/tokens'

import { UserGeometryChanged } from '@/domain/events/UserGeometryChanged'
import { UserGeometrySynced } from '@/domain/events/UserGeometrySynced'
import { UserGeometry } from '@/domain/models/UserGeometry/UserGeometry'
import { MapLayerState, MapLayerStateProps } from '@/domain/states/MapLayerState'
import { LoadUserGeometriesUseCase } from '@/domain/useCases/UserGeometries/LoadUserGeometriesUseCase'

import { Icon } from '../AirspacesTool/AirspacesTool.styles'
import { ToolContainer } from '../ToolbarTools.styles'

import { MetricsEvents } from '@/constants/metricsEvents'
import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { useI18n } from '@/presentation/hooks/useI18n'
import { openDrawer } from '@/presentation/router/DrawerRouter'
import { RouterPaths } from '@/presentation/router/RouterPathsMapper'
import { sendMetrics } from '@/presentation/utils/sendMetrics'
import { GlobalToastUtil } from '@/utils/globalToastUtil'

// Mirrors MapLayerState's private MAX_ACTIVE_USER_GEOMETRIES cap so the toolbar
// can surface a message instead of silently no-opping at the limit.
const MAX_ACTIVE_USER_GEOMETRIES = 100

const USER_GEOMETRIES_UPLOAD_PATH = `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_GEOMETRIES}/${RouterPaths.MY_CONTENT__USER_GEOMETRIES__UPLOAD}`

function UserGeometriesTool() {
  const { t } = useI18n()

  const [mapLayerState, mapLayerStateMutator] = useGlobalState<MapLayerState, MapLayerStateProps>(
    InjectionTokens.MapLayerState
  )
  const loadUserGeometries = useInjection<LoadUserGeometriesUseCase>(InjectionTokens.LoadUserGeometriesUseCase)

  const [geometries, setGeometries] = useState<UserGeometry[]>([])

  const activeGeometryIds = useMemo(
    () => new Set(mapLayerState.userGeometries.map((geometry) => geometry.id)),
    [mapLayerState.userGeometries]
  )

  const fetchGeometries = useCallback(async () => {
    const result = await loadUserGeometries.execute()
    if (result.isFailure) {
      setGeometries([])
      return
    }
    setGeometries(result.getValue())
  }, [loadUserGeometries])

  useEffect(() => {
    fetchGeometries()
  }, [fetchGeometries])

  useEffect(() => {
    const changedSubscription = UserGeometryChanged.subscribe(() => fetchGeometries())
    const syncedSubscription = UserGeometrySynced.subscribe(() => fetchGeometries())

    return () => {
      changedSubscription.unsubscribe()
      syncedSubscription.unsubscribe()
    }
  }, [fetchGeometries])

  const handleAddGeometry = useCallback(() => {
    openDrawer(USER_GEOMETRIES_UPLOAD_PATH)
  }, [])

  const handleToggleGeometry = useCallback(
    (geometry: UserGeometry) => {
      const id = geometry.id.toString()

      if (activeGeometryIds.has(id)) {
        mapLayerStateMutator.removeActiveUserGeometry(id)
        sendMetrics(MetricsEvents.USER_GEOMETRIES_TOGGLED, { origin: 'toolbar', state: 'off' })
        return
      }

      if (mapLayerState.userGeometries.length >= MAX_ACTIVE_USER_GEOMETRIES) {
        GlobalToastUtil.send({
          label: t('USER-GEOMETRIES_TOAST_ACTIVE-LIMIT'),
          variant: 'error',
          dismissible: true
        })
        return
      }

      mapLayerStateMutator.addActiveUserGeometry({
        id,
        name: geometry.name,
        // Web handle to the locally stored .fgb blob; the map renderer reads it by id.
        uri: id
      })
      sendMetrics(MetricsEvents.USER_GEOMETRIES_TOGGLED, { origin: 'toolbar', state: 'on' })
    },
    [activeGeometryIds, mapLayerState.userGeometries.length, mapLayerStateMutator, t]
  )

  return (
    <ToolContainer gap={12}>
      <MenuItem
        key="add-user-geometry"
        title={t('TOOLBAR_USER-GEOMETRIES_ADD-ITEM_LABEL')}
        size="sm"
        rightDecorator={
          <Icon>
            <Assets.Plus size={'sm'} color={'#FCFCFC'} />
          </Icon>
        }
        onClick={handleAddGeometry}
      />
      <MenuGroup>
        {geometries.map((geometry) => {
          const id = geometry.id.toString()
          return (
            <MenuItem
              key={id}
              title={geometry.name}
              size="sm"
              hasSwitch
              isSwitchChecked={activeGeometryIds.has(id)}
              onClick={() => handleToggleGeometry(geometry)}
            />
          )
        })}
      </MenuGroup>
    </ToolContainer>
  )
}

export { UserGeometriesTool }
